import { chain } from 'lodash';

import { updateReferencesListForPreviewMode, customFootnoteOrDefault } from '~/utils/footnotes';
import { normalizeReferenceIdentity, getSecondOrderReferenceSource } from '~/utils/citation-second-order';
import { CITATION_TAG_COLORS } from '~/utils/citation-source';

const computePositionBasedOnNeighbors = (
  top,
  left,
  bulletPositions,
  bulletSize = 24,
  verticalOffset = -4,
  useBoxCollision = false,
) => {
  // Calculate initial position
  let bulletTop = top + verticalOffset;
  let bulletLeft = left;

  // Check for overlaps and adjust position
  const minDistance = bulletSize; // Minimum distance between bullets
  const boxHeight = 18; // gated tag height — see TAG_HEIGHT in renderCitationBulletsOverIframe

  let attempts = 0;
  const maxAttempts = 8; // Try 8 different positions around the original

  while (attempts < maxAttempts) {
    let hasOverlap = false;

    // Check against existing bullet positions. Legacy's circles use a circular radius check
    // (diagonal distance), which is fine for isolated circles. Gated's tags are short and wide, so
    // that same radius flags bullets on separate, nearby text lines as "colliding" even when their
    // actual boxes never touch — a false positive that cascades bullets diagonally down-right across
    // unrelated lines. useBoxCollision checks the real rectangle overlap instead.
    for (const existingPos of bulletPositions) {
      const hasBoxOverlap =
        Math.abs(bulletTop - existingPos.top) < boxHeight && Math.abs(bulletLeft - existingPos.left) < bulletSize;
      const distance = Math.sqrt(Math.pow(bulletTop - existingPos.top, 2) + Math.pow(bulletLeft - existingPos.left, 2));

      if (useBoxCollision ? hasBoxOverlap : distance < minDistance) {
        hasOverlap = true;
        break;
      }
    }

    if (!hasOverlap) {
      break; // No overlap found, use this position
    }

    // Try different offset positions around the original
    const offset = Math.floor(attempts / 2) + 1;
    const direction = attempts % 2 === 0 ? 1 : -1;

    if (attempts < 4) {
      // Try horizontal offsets first
      bulletLeft = left + offset * direction * 30;
    } else {
      // Then try vertical offsets
      bulletTop = top + verticalOffset + offset * direction * 30;
      bulletLeft = left; // Reset to original left position
    }

    attempts++;
  }
  return { bulletTop, bulletLeft };
};

// Transform doc-reference spans to superscript citations in preview mode
export const transformDocReferences = (htmlContent, footnotes, showSecondOrderReferences = false) => {
  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = htmlContent;

  removeCitationBulletsOverlay({ container: tempContainer });

  const CITATION_SEL = 'span[data-doc-id]:not(.transformed)';
  const isCitationEl = (el) => el?.nodeType === 1 && el.matches?.(CITATION_SEL);
  // matches spaces and . , (including non-breaking spaces). These are allowed to sit between citations and still count as “consecutive”.
  const isSkippableText = (n) =>
    n?.nodeType === Node.TEXT_NODE && /^[\s.,\u00A0\u202F\u2009]+$/.test(n.nodeValue || '');

  // 2) Compress consecutive numbers into ranges
  const compress = (nums) =>
    chain(nums)
      .uniq()
      .sortBy()
      .transform((acc, n) => {
        const last = acc[acc.length - 1];
        if (!last || n !== last[1] + 1) acc.push([n, n]);
        else last[1] = n;
      }, [])
      .map(([s, e]) => (s === e ? `${s}` : e === s + 1 ? `${s},${e}` : `${s}–${e}`))
      .value();

  const nextGroup = (start) => {
    const group = [start];
    const between = [];
    let buf = [];
    let p = start.nextSibling;

    while (p) {
      if (isCitationEl(p)) {
        group.push(p);
        between.push(...buf);
        buf = [];
        p = p.nextSibling;
        continue;
      }
      if (isSkippableText(p)) {
        buf.push(p);
        p = p.nextSibling;
        continue;
      }
      break; // hit real content → stop; keep trailing buf
    }
    return { group, between };
  };

  const dict = {};
  let next = 1;
  // Display text for the printed reference list (index i = number i+1), built fresh from the
  // citation spans each pass rather than recovered from rendered text — matching titles back to
  // footnotes is ambiguous when one title is a substring of another.
  const referenceLines = [];

  // Resolves the number(s) a citation span contributes to its <sup>. A genuine second-order
  // reference replaces the main document's own number, so one span can contribute several numbers,
  // each assigned the next-available value in document order and reused if repeated elsewhere.
  const resolveSpanNums = (span) => {
    const id = span.getAttribute('data-doc-id');
    if (!id) return [];

    const reference = footnotes?.find((ref) => (ref.externalId || ref.id) === id);
    let genuineSources = [];

    if (showSecondOrderReferences && reference) {
      const page = span.getAttribute('data-page');
      const snippetIndex = span.getAttribute('data-index');
      const snippetKey = `${Number(page)}-${Number(snippetIndex)}`;
      const ownExternalId = reference.externalId || reference.id;
      const sources = getSecondOrderReferenceSource({
        reference,
        snippetKey,
        currentResourceDocType: reference?.citation_metadata?.doc_type || '',
      });

      // Sources removed individually from this span's overflow list (see
      // renderCitationBulletsOverIframe) — excluded here too so Preview's numbering matches what
      // the live bullet overlay and the printed reference list already show.
      let excludedSourceKeys = [];
      try {
        excludedSourceKeys = JSON.parse(span.getAttribute('data-excluded-second-order-refs') || '[]');
      } catch {
        // malformed attribute value — keep the default empty array
      }

      genuineSources = sources.filter(
        (source) =>
          source.externalId !== ownExternalId &&
          !excludedSourceKeys.includes(normalizeReferenceIdentity(source.text) || source.referenceId || ''),
      );
    }

    if (genuineSources.length) {
      return genuineSources.map((source) => {
        // Keyed by text alone, not id — the same source cited by several parent documents (e.g. two
        // ads citing the same SPC) still gets one shared reference-list number, like a bibliography.
        // referenceId is only a fallback for entries with no text.
        const key = normalizeReferenceIdentity(source.text) || source.referenceId || '';
        if (!dict[key]) {
          dict[key] = next++;
          // A user-edited second-order source (secondOrderCustomText, set on every parent citing it)
          // overrides the AI-extracted text. No page suffix — a source can be cited on several pages
          // of its parent, and this collapsed numbering only tracks one shared entry for all of them.
          referenceLines[dict[key] - 1] = reference.secondOrderCustomText?.[key] || source.text;
        }
        return dict[key];
      });
    }

    if (!(id in dict)) {
      dict[id] = next++;
      // Bare display text (no leading index — updateReferencesListForPreviewMode adds its own,
      // matching the numbering it just assigned above), with no page suffix — Preview never shows
      // citing pages.
      referenceLines[dict[id] - 1] = reference ? customFootnoteOrDefault([reference], false, false)[0] : id;
    }
    return [dict[id]];
  };

  Array.from(tempContainer.querySelectorAll('span[data-doc-id]')).forEach((s) => {
    if (!isCitationBulletVisibleOnDoc(s)) return; // skip hidden citations
    resolveSpanNums(s);
  });

  for (let cur; (cur = tempContainer.querySelector(CITATION_SEL)); ) {
    if (!cur.parentNode) break;

    const { group, between } = nextGroup(cur);

    // unique ids for attribute; numbers resolved per span (possibly several each); compressed text
    const ids = [...new Set(group.map((s) => s.getAttribute('data-doc-id')))];
    const nums = group.flatMap((s) => resolveSpanNums(s));

    const sup = document.createElement('sup');
    sup.textContent = compress(nums).join(',');
    sup.setAttribute('data-doc-ids', ids.join(','));

    const first = group[0];
    first.classList.add('transformed');
    first.parentNode.replaceChild(sup, first);

    group.slice(1).forEach((n) => n.remove()); // remove extra spans
    if (group.length > 1) between.forEach((n) => n.remove()); // remove only BETWEEN punctuation
  }

  // 4) Update references once
  tempContainer.querySelectorAll('[data-ant-field="reference-list"]').forEach((child) => {
    const textElement = child.querySelector('p');
    updateReferencesListForPreviewMode(textElement, referenceLines);
  });

  return tempContainer.innerHTML;
};

export const removeCitationBulletsOverlay = ({ iframeDoc, container }) => {
  let existingOverlay = iframeDoc?.getElementById('citation-bullets-overlay');
  if (container) {
    existingOverlay = container.querySelector('#citation-bullets-overlay');
  }
  if (existingOverlay) {
    existingOverlay.remove();
  }
};

// Max citation bullets/badges drawn individually per citation position (in authoring and Assist)
// before collapsing the rest into one "+N" bullet/badge.
export const MAX_INDIVIDUAL_BULLETS = 3;

const escapeHtml = (str = '') =>
  String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Clips the quote in plain JS, not CSS line-clamp (MJML boilerplate can !important-reset that away).
// "…" is added unconditionally since stored snippets often already end mid-sentence. Escapes AFTER
// clipping — raw OCR text can contain a stray "<" that would otherwise start an unclosed HTML tag.
const SNIPPET_MAX_CHARS = 220; // roughly 3 lines at this card's font-size/width
const truncateSnippet = (text = '') => {
  const trimmed = String(text).trim();
  const truncated = trimmed.length > SNIPPET_MAX_CHARS ? trimmed.slice(0, SNIPPET_MAX_CHARS).trimEnd() : trimmed;
  return escapeHtml(truncated);
};

// Mirrors ReferenceDetailsLabels.vue's labels row (type tag + doc name/logo + doc type, "·"-separated),
// modeled on CitationInfoLabel.vue with showIcon=false. Unsubstantiated shows "No citation found · {type}"
// instead of the bare type, matching CitationInfoLabel's own fallback wording.
const buildSourceLabelsRowHtml = (source) => {
  if (!source) return '';

  // The type word (Internal/External/Manually added) is wrapped in its own span so it alone gets
  // capitalized/weighted to match .tooltip-source-name (Veeva) — capitalizing the whole pill text
  // would also capitalize every word of the "No citation found" prefix ("No Citation Found").
  const typeWordHtml = `<span class="tooltip-pill-type">${escapeHtml(source.type || '')}</span>`;
  const typeLabel = source.unsubstantiated ? `No citation found · ${typeWordHtml}` : typeWordHtml;
  const pillClass = `citation-pill${source.unsubstantiated ? ' citation-pill--unsubstantiated' : ''}`;
  const nameHtml = source.name
    ? `<span>·</span><span class="tooltip-source-name">${
        source.logo ? `<img class="citation-tag-logo" src="${source.logo}" alt="${escapeHtml(source.name)}"/>` : ''
      }${escapeHtml(source.name)}</span>`
    : '';
  // .tooltip-doc-type capitalizes this the same way ReferenceDetailsLabels.vue's own doc-type
  // segment does (its .reference-doc class has text-transform: capitalize) — the raw
  // citation_metadata.doc_type value is stored lowercase, and without this it would render as-is.
  const docTypeHtml = source.docType
    ? `<span>·</span><span class="tooltip-doc-type">${escapeHtml(source.docType)}</span>`
    : '';

  return `<div class="tooltip-labels"><span class="${pillClass}">${typeLabel}</span>${nameHtml}${docTypeHtml}</div>`;
};

// Mirrors ReferenceDetails.vue's "Reference found on" row — shown only for a genuine second-order
// reference (not unsubstantiated, not the citing doc itself), pointing at the page where that source
// itself was found. Appears alongside the passage row below; data-action wires the shared click handler.
const buildReferenceFoundOnRowHtml = ({ name, page }) =>
  page == null
    ? ''
    : `
  <div class="tooltip-passage-row" data-action="open-full-doc" data-page="${page}">
    <span class="tooltip-subtitle">REFERENCE FOUND ON</span><span>·</span>
    <span class="tooltip-link">${escapeHtml(name || '')}</span><span>&gt;</span>
    <span class="tooltip-link">p. ${page}</span>
  </div>`;

// Mirrors ReferenceDetails.vue's per-passage row — always shown for any non-custom-reference
// citation, pointing at the page in the citing document where this passage was quoted.
// data-action="open-full-doc" wires the shared delegated click handler below.
const buildPassageRowHtml = ({ name, page }) => `
  <div class="tooltip-passage-row" data-action="open-full-doc" data-page="${page}">
    <span class="tooltip-subtitle">PASSAGE 1</span><span>·</span>
    <span class="tooltip-link">${escapeHtml(name || '')}</span><span>&gt;</span>
    <span class="tooltip-link">p. ${page}</span>
  </div>`;

// Positional, nth-child-based selector for an arbitrary live element — mirrors wesley-helpers'
// own cssPath builder closely enough for Wesley's insertAfter/removeElement (which just resolve
// `query` via a plain document.querySelector — see wesley/elements.js's getElement) to act on the
// exact element we mean, even when several citation spans share identical data-doc-id/page/index.
// Computed fresh right before use, never persisted — purely positional, so it only needs to be
// valid for the single synchronous DOM read it's built from.
const buildCssPath = (el) => {
  const segments = [];
  let node = el;
  while (node && node.nodeType === 1 && node.tagName !== 'BODY') {
    const parent = node.parentElement;
    if (!parent) break;
    const index = Array.from(parent.children).indexOf(node) + 1;
    segments.unshift(`${node.tagName.toLowerCase()}:nth-child(${index})`);
    node = parent;
  }
  return `body > ${segments.join(' > ')}`;
};

export const renderCitationBulletsOverIframe = async ({
  iframeDoc,
  mapCitationsDataFromFootnote,
  openDocument,
  saveEditorContentStateFromIframe,
  saveHtmlDocumentContentToDb,
  refreshReferencesOnDelete,
  updateEditorSelectableElementsData,
  // Gates the Discover-aligned rounded-tag treatment (solid/dashed border, source icon, enriched
  // click-through card) vs the legacy purple/green circle bullet — same flag (isSecondOrderReferencesOrg)
  // and naming convention as transformDocReferences' own showSecondOrderReferences param below.
  showSecondOrderReferences = false,
  // True only on render passes known to be safe for structural DOM changes (no field under active
  // Tiptap control) — see the comment on renderCitationTooltipsInIframe in useDocumentEditor.js.
  // Gates reconcileCitationSiblings below; every other call site keeps today's non-mutating
  // rendering regardless of this flag.
  allowStructuralEdits = false,
  // Replace a rich-text field's whole content via Wesley's own setContent API (see
  // useDocumentEditor.js). mj-text/mj-button are registered as richTextElements with an EMPTY
  // allowed-children list (wesley/extensions/mjml/index.js) — Wesley deliberately refuses
  // insertAfter/removeElement there ("span tag not supported in mj-text"); setContent is the only
  // sanctioned way to change their content structurally. Tracked by Wesley's Citation node schema
  // (wesley repo) once re-parsed, so it survives future edit sessions, unlike a raw DOM mutation.
  // Undefined when the host hasn't wired this through, in which case reconciliation is skipped.
  setFieldContent,
}) => {
  if (!iframeDoc) return;

  // Reentrancy guard. This function now does real async work per span — mapCitationsDataFromFootnote,
  // and, when reconciling, a setFieldContent message round-trip — so a later invocation (the next
  // keystroke, a click/select settling) can easily start and even finish
  // while an earlier one is still mid-flight. Without this, the later call's removeCitationBulletsOverlay
  // rips out the shared #citation-bullets-overlay div while the earlier call is still building bullets
  // destined for it — those bullets get appended into a now-detached node and silently never render,
  // even though the real citation spans (which live in the actual document, not the overlay) are
  // completely unaffected. Stamped on iframeDoc itself since that's the one object stable across
  // separate invocations/closures of this function.
  const myGeneration = (iframeDoc.__citationRenderGeneration = (iframeDoc.__citationRenderGeneration || 0) + 1);
  const isCurrent = () => iframeDoc.__citationRenderGeneration === myGeneration;

  removeCitationBulletsOverlay({ iframeDoc });

  const overlay = iframeDoc.createElement('div');
  overlay.id = 'citation-bullets-overlay';
  Object.assign(overlay.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '9999',
  });
  iframeDoc.body.appendChild(overlay);

  // Remove any previous font-link/style by id before re-adding (this reruns on nearly every content
  // change) rather than skip-if-exists, so a later tagStyle edit isn't frozen in for the iframe's
  // lifetime. Stable ids also let createHtmlHistory.js strip this scaffolding before saving content.
  iframeDoc.getElementById('citation-bullets-font-link')?.remove();
  const fontLink = iframeDoc.createElement('link');
  fontLink.id = 'citation-bullets-font-link';
  fontLink.rel = 'stylesheet';
  fontLink.href = 'https://fonts.googleapis.com/css2?family=DM+Sans&display=swap';
  iframeDoc.head.appendChild(fontLink);

  // DM Sans loads asynchronously (display=swap) — a render that happens before it's ready draws
  // bullet labels (and, since the injected CSS applies DM Sans to the whole document via `*`, the
  // real document text too) with fallback-font metrics, which can shift once the real font applies
  // and the surrounding text reflows. Schedule one corrective re-render for when the font actually
  // finishes loading, rather than leaving it to chance.
  //
  // Gated on a once-per-iframe flag, not `fonts.status !== 'loaded'` (removed — that status can
  // already read 'loaded' almost immediately when DM Sans is cached from an earlier page load in
  // the same browser session, which skipped this whole corrective block even though THIS iframe's
  // own layout may not have actually settled with it applied yet). But it still must only be
  // SCHEDULED ONCE per iframe, not once per render call: this function runs on nearly every
  // keystroke, and without this guard every single one of those calls would schedule its own
  // corrective follow-up a couple of frames later, cascading into exactly the flicker (bullets
  // torn down and rebuilt repeatedly while typing, settling only once input stops and the backlog
  // of scheduled corrections drains) this was observed causing.
  if (iframeDoc.fonts && !iframeDoc.__citationFontsCorrectionScheduled) {
    iframeDoc.__citationFontsCorrectionScheduled = true;
    iframeDoc.fonts.ready.then(() => {
      // fonts.ready resolving doesn't guarantee the browser has already applied the reflow that
      // follows from the new font's metrics — without waiting a couple of animation frames first,
      // this "corrective" render can itself run before that repaint lands, measuring the same
      // stale (pre-font) positions it was meant to fix. Two rAFs, not one: the first is only
      // guaranteed to run before the next repaint, not necessarily after the font-driven reflow is
      // reflected in it; the second fires after that repaint has actually happened.
      const iframeWindowRef = iframeDoc.defaultView;
      const afterRepaint = (cb) => iframeWindowRef.requestAnimationFrame(() => iframeWindowRef.requestAnimationFrame(cb));
      afterRepaint(() => {
        renderCitationBulletsOverIframe({
          iframeDoc,
          mapCitationsDataFromFootnote,
          openDocument,
          saveEditorContentStateFromIframe,
          saveHtmlDocumentContentToDb,
          refreshReferencesOnDelete,
          updateEditorSelectableElementsData,
          showSecondOrderReferences,
          allowStructuralEdits,
          setFieldContent,
        }).catch((e) => console.error('Corrective post-font-load render failed', e));
      });
    });
  }

  iframeDoc.getElementById('citation-bullets-style')?.remove();
  const styleTag = iframeDoc.createElement('style');
  styleTag.id = 'citation-bullets-style';
  styleTag.innerHTML = tagStyle;
  iframeDoc.head.appendChild(styleTag);

  const spans = Array.from(iframeDoc.querySelectorAll('span[data-doc-id]'));

  // Step 1: Precompute all bullet info
  const dict = {};
  let label = 1;

  // The anchoring span is a zero-width marker right after the preceding text, so with no gap the
  // first bullet's left edge lands exactly where the text ends, reading as overlapping the last
  // character. Gated bullets get a small breathing-room gap; legacy keeps its original 0 so its
  // spacing is untouched.
  const GROUP_LEADING_GAP = showSecondOrderReferences ? 4 : 0;
  // Each bullet's own width depends on its label length — "4" needs far less room than "23" or
  // "+7" — so a single-digit cluster stays as tight as legacy's own spacing, while a double-digit
  // (or "+N") bullet gets the extra width it actually needs, instead of every bullet in the
  // cluster paying for the widest possible case via one flat width.
  const TAG_WIDTH_SINGLE = 24;
  // 28, not a rounder/more generous number: a reconciled span's real reserved space is Wesley's own
  // fixed margin-right (28px, untouchable — see the note on reconciled.forEach above, writing to a
  // reconciled span's style breaks it), so this is the largest a double-digit/"+N" bullet can draw
  // without spilling into whatever comes right after it. Slightly tight for a source with both a
  // logo icon and a 2-digit label (not clipped — .citation-tag has no overflow:hidden — just a
  // small visual nudge past its own box edge in that specific combination), but that's preferable
  // to either overlap (TAG_WIDTH_DOUBLE wider than 28) or touching the span itself to compensate.
  const TAG_WIDTH_DOUBLE = 28;
  const TAG_GAP = 4;
  const getTagWidth = (label) => (String(label).length >= 2 ? TAG_WIDTH_DOUBLE : TAG_WIDTH_SINGLE);
  // Lifted out of the per-span loop below, which uses these for vertical centering.
  const TAG_HEIGHT = 18;
  const ALIGNMENT_NUDGE = -2;

  // Right edge of the citation's nearest block-level container (skipping inline ancestors), not
  // the whole iframe width, since text (and so citations) can sit in a narrower column (e.g. beside
  // an image) — this is what computeVisibleCount below checks bullets against. Falls back to the
  // iframe's own width if no block ancestor is found.
  const getRowBoundary = (anchorSpan, anchorScrollLeft) => {
    let el = anchorSpan.parentElement;
    while (el && iframeDoc.defaultView.getComputedStyle(el).display === 'inline') {
      el = el.parentElement;
    }
    const rect = el ? el.getBoundingClientRect() : { right: iframeDoc.documentElement.clientWidth };
    return rect.right + anchorScrollLeft;
  };

  // Positions a whole group of entries left-to-right on one row — safe to assume they all fit
  // (computeVisibleCount below already caps how many entries are passed in to whatever fits within
  // rowBoundary, so there's nothing left here to wrap). getWidth/gap default to the gated per-label
  // width + TAG_GAP; legacy's circle is a fixed 24px regardless of label length (unlike gated's
  // 24/30 split), so it passes its own resolver + gap (24 + 2 = 26, the original flat
  // BULLET_GROUP_STEP this replaced) to keep its spacing identical to before this session's
  // per-label-width change, which was otherwise picking up gated's wider 30px double-digit case for
  // legacy's circles too.
  const withGroupOffsets = (entries, { baseTop, getWidth = getTagWidth, gap = TAG_GAP }) => {
    let cumulativeLeft = GROUP_LEADING_GAP;
    return entries.map((entry, index) => {
      if (index > 0) {
        cumulativeLeft += getWidth(entries[index - 1].label) + gap;
      }
      return {
        ...entry,
        left: entry.left + cumulativeLeft,
        top: baseTop,
        tagWidth: getWidth(entry.label),
      };
    });
  };

  // How many of this citation's bullets can actually be drawn individually before running past
  // rowBoundary, so the rest collapse into the "+N" badge instead of being positioned past the edge
  // (which is what let bullets overlap whatever real text followed, when MAX_INDIVIDUAL_BULLETS was
  // a flat cap regardless of how much room was actually left on that line). Read-only — unlike an
  // earlier version of this fix, which inserted real placeholder elements into the live document to
  // reserve space: Wesley (the web-editor iframe this content lives in) periodically rebuilds a
  // field's DOM from its own content model whenever its inline edit session for that field ends
  // (e.g. the user selects elsewhere), and lost the citation span itself along with those injected
  // elements when it did. This never touches the live DOM, only arithmetic over tag widths already
  // computed from getTagWidth, so there's nothing for Wesley's own model to conflict with. Always
  // keeps at least 1 visible even if it doesn't fit, rather than showing nothing.
  const computeVisibleCount = (entries, left, rowBoundary) => {
    const maxVisible = Math.min(entries.length, MAX_INDIVIDUAL_BULLETS);
    if (rowBoundary == null) return maxVisible;

    const widthOf = (visible) => {
      const labels = entries.slice(0, visible).map((entry) => entry.label);
      if (visible < entries.length) labels.push(`+${entries.length - visible}`);
      return labels.reduce((sum, label, index) => sum + getTagWidth(label) + (index > 0 ? TAG_GAP : 0), GROUP_LEADING_GAP);
    };

    for (let visible = maxVisible; visible >= 1; visible--) {
      if (left + widthOf(visible) <= rowBoundary) return visible;
    }
    return 1;
  };

  // mj-text/mj-button content is edited as one rich-text blob (richTextElements in
  // wesley/extensions/mjml/index.js) — Wesley's own insertAfter/removeElement explicitly refuse to
  // add or remove structural children there (tagIsSupported returns an empty allow-list for these
  // tags: "span tag not supported in mj-text"). setContent (a full innerHTML replacement) is the
  // only sanctioned way to change this content, so every mutation below happens on a DETACHED clone
  // of the field, never the live DOM directly — the live DOM only changes once the resulting HTML
  // is sent through setFieldContent.
  const findRichTextAncestor = (span) => {
    let el = span.parentElement;
    while (el && el.tagName !== 'MJ-TEXT' && el.tagName !== 'MJ-BUTTON') {
      el = el.parentElement;
    }
    return el;
  };

  // Marks `liveElements` (a plain attribute mutation, not structural — same as data-label/
  // data-excluded-second-order-refs elsewhere here, so it needs no Wesley API call of its own),
  // clones richTextEl, resolves each marked element's clone counterpart (cloneNode doesn't preserve
  // node identity, only structure/attributes), then unmarks the live elements again. Returns the
  // clone and its resolved elements for the caller to mutate before reading clone.innerHTML.
  let reconcileMarkerSeq = 0;
  const cloneForSplice = (richTextEl, liveElements) => {
    const markerId = `reconcile-${++reconcileMarkerSeq}`;
    liveElements.forEach((el, i) => el?.setAttribute('data-reconcile-marker', `${markerId}-${i}`));
    const clone = richTextEl.cloneNode(true);
    liveElements.forEach((el) => el?.removeAttribute('data-reconcile-marker'));
    const clonedElements = liveElements.map((el, i) => {
      if (!el) return null;
      const marked = clone.querySelector(`[data-reconcile-marker="${markerId}-${i}"]`);
      marked?.removeAttribute('data-reconcile-marker');
      return marked;
    });
    return { markerId, clone, clonedElements };
  };

  // Ensures one real citation-span sibling exists per second-order entry, each tagged
  // data-second-order-source-key so a future edit session's Tiptap round-trip keeps recognizing it
  // (see citation.js in the wesley repo) — instead of relying on JS-simulated offsets over space
  // nothing in the real document reserves. No separate content-less "anchor" span is kept around:
  // the first entry's own span physically replaces anchorSpan's position, so every entry, including
  // the first, is an equally real, independently addable/removable sibling with nothing left over.
  // Returns one real DOM element per entry, in order (same length as `entries`, no extra element),
  // once every entry is backed by a real node, or null if reconciliation can't proceed this pass (no
  // sourceKey on some entry, no recognized rich-text ancestor, or setFieldContent didn't produce the
  // expected result) — callers fall back to the simulated layout in that case.
  const reconcileCitationSiblings = async (anchorSpan, entries) => {
    if (entries.some((entry) => !entry.sourceKey)) return null;

    const richTextEl = findRichTextAncestor(anchorSpan);
    if (!richTextEl) return null;

    const docId = anchorSpan.getAttribute('data-doc-id');
    const page = anchorSpan.getAttribute('data-page');
    const snippetIndex = anchorSpan.getAttribute('data-index');
    // Same doc-id/page/index alone isn't quite unique — two genuinely separate citation points can
    // coincidentally share all three (same source quoted twice at the same spot in two different
    // passages). data-snippet (the actual quoted text) disambiguates them; '' so a citation with no
    // snippet set still matches another with none, rather than null !== null failing loudly.
    const snippet = anchorSpan.getAttribute('data-snippet') || '';

    const matchesCluster = (el) =>
      el?.tagName === 'SPAN' &&
      el.getAttribute('data-doc-id') === docId &&
      el.getAttribute('data-page') === page &&
      el.getAttribute('data-index') === snippetIndex &&
      (el.getAttribute('data-snippet') || '') === snippet;

    // The whole existing cluster, in document order, starting from anchorSpan itself (which, on
    // the very first reconciliation pass, is the original plain citation span; on later passes,
    // it's whatever the main loop found as this cluster's first span — possibly already one of our
    // own reconciled entries, carrying a source key of its own).
    const existing = [anchorSpan];
    let node = anchorSpan.nextElementSibling;
    while (matchesCluster(node)) {
      existing.push(node);
      node = node.nextElementSibling;
    }

    const desiredKeys = entries.map((entry) => entry.sourceKey);
    const existingKeys = existing.map((el) => el.getAttribute('data-second-order-source-key'));
    if (desiredKeys.length === existingKeys.length && desiredKeys.every((key, i) => key === existingKeys[i])) {
      return existing; // already reconciled — nothing to change this pass
    }

    const { markerId, clone, clonedElements } = cloneForSplice(richTextEl, existing);
    const [clonedFirst, ...clonedRest] = clonedElements;
    if (!clonedFirst) return null;

    // Insert the desired set fresh, right after the existing cluster's first element — then remove
    // the entire old cluster (first element included), leaving only the new spans in its place.
    // Simpler and just as correct as diffing kept-vs-new, since this clone (and any node identity
    // within it) is discarded the moment its innerHTML is read below.
    let insertAfter = clonedFirst;
    desiredKeys.forEach((key, index) => {
      const el = iframeDoc.createElement('span');
      el.className = 'citation-span';
      el.setAttribute('data-doc-id', docId);
      el.setAttribute('data-page', page);
      el.setAttribute('data-index', snippetIndex);
      if (snippet) el.setAttribute('data-snippet', snippet);
      el.setAttribute('data-second-order-source-key', key);
      el.setAttribute('data-reconcile-marker', `${markerId}-new-${index}`);
      insertAfter.insertAdjacentElement('afterend', el);
      insertAfter = el;
    });
    clonedFirst.remove();
    clonedRest.forEach((el) => el?.remove());

    await setFieldContent(buildCssPath(richTextEl), clone.innerHTML);

    // The field's entire innerHTML was just replaced, so every node in `existing` (anchorSpan
    // included) is now detached and stale — re-resolve the live replacements via the markers just
    // sent through, then strip them (a plain attribute mutation, no further Wesley API call needed).
    const resultSpans = [];
    for (let index = 0; index < desiredKeys.length; index++) {
      const el = richTextEl.querySelector(`[data-reconcile-marker="${markerId}-new-${index}"]`);
      if (!el) return null; // setContent didn't land as expected — bail to the safe fallback
      el.removeAttribute('data-reconcile-marker');
      resultSpans.push(el);
    }

    return resultSpans;
  };

  // Sequential on purpose (not Promise.all): labels must be assigned in true document order, so a
  // second-order bullet's number reflects where it falls, not when its promise happened to resolve.
  const bulletInfos = [];
  // Real sibling spans from a previous reconciliation pass (see reconcileCitationSiblings) that
  // belong to a citation cluster already being processed this pass — every entry, including the
  // first, is now an equally real span (no separate content-less "anchor" span), so there's no
  // attribute to statically tell "is this a cluster start" from "is this a continuation" the way
  // `data-second-order-source-key` used to for entries 1+. Populated once a cluster's actual size
  // is known (from citationEntries.length, below) by walking forward from its first span.
  const consumedSpans = new Set();

  for (const span of spans) {
    // Superseded by a later invocation while we were awaiting a previous span's work — stop doing
    // further (possibly conflicting) reconciliation/DOM work for a pass whose output won't be used.
    if (!isCurrent()) {
      return;
    }

    if (consumedSpans.has(span)) {
      continue;
    }

    // top is 0 only when element is hidden from layout
    if (span.getBoundingClientRect().top === 0) {
      continue;
    }

    const id = span.getAttribute('data-doc-id');
    const snippetIndex = span.getAttribute('data-index');
    let pageData = span.getAttribute('data-page');
    // Keyed by id+page (not id alone) — the editor's own live-bullet numbering stays scoped to
    // document+page while actively editing, deliberately separate from Preview/export's collapsed
    // (document-wide) numbering; the two only reconcile once you preview or save.
    const dictKey = `${id}-${pageData}`;

    let snippetData = span.getAttribute('data-snippet');
    let updatedByUser = span.getAttribute('data-updated-by-user') === 'true';

    // Sources the user removed individually from this span's overflow list (see per-row delete
    // below) — persisted as a plain attribute on the span so it rides along through the normal
    // save pipeline with no separate storage.
    let excludedSourceKeys = [];
    try {
      excludedSourceKeys = JSON.parse(span.getAttribute('data-excluded-second-order-refs') || '[]');
    } catch {
      // malformed attribute value — keep the default empty array
    }

    // mapCitationsDataFromFootnote returns one entry per citation to render for this span — for
    // gated orgs, a snippet can have several second-order references, each getting its own
    // bullet+tooltip (as in Assist), instead of just showing the first one.
    const citationEntries = await mapCitationsDataFromFootnote(id, pageData, snippetData, snippetIndex, excludedSourceKeys);

    // Don't remove the span here — other consumers (footnotes mapping, preview transforms) still
    // need it. Clear any margin-right reserved by earlier approaches — any real reserved space in the
    // line still has to fit or wraps the whole element; bullets are positioned via the overlay instead.
    span.style.removeProperty('margin-right');

    if (!citationEntries?.length) {
      // Skip rendering a bullet for this span.
      continue;
    }

    // Any real sibling spans already reconciled for this cluster (from a previous pass) sit
    // immediately after `span` in document order, matching the same doc-id/page/index/snippet.
    // Claim exactly as many as citationEntries now says this cluster needs, so the outer loop's own
    // later iteration over `spans` doesn't reprocess them as independent citations. data-snippet
    // (not just doc-id/page/index) matters here too: two genuinely separate citation points can
    // coincidentally share all three if the same source is quoted twice at the same spot in two
    // different passages — the actual quoted text is what tells them apart.
    //
    // Anything BEYOND that count is pruned here too — not just claimed. reconcileCitationSiblings
    // below only ever runs for clusters with >1 entries, so if deleting a second-order source (via
    // either the in-text per-row delete or the references panel) shrinks a cluster down to 1 entry
    // or fewer, nothing else would ever clean up its now-orphaned extra siblings: each would
    // otherwise sit there forever, independently reprocessed as if it were its own separate
    // citation next render. Pruning (not just claiming) only happens on an allowStructuralEdits
    // pass — like every other DOM removal in this file, safe regardless of typing state, but kept
    // behind the same gate as reconciliation for consistency.
    {
      const wantedCount = Math.max(citationEntries.length - 1, 0);
      let node = span.nextElementSibling;
      let count = 0;
      while (
        node?.tagName === 'SPAN' &&
        node.getAttribute('data-doc-id') === id &&
        node.getAttribute('data-page') === pageData &&
        node.getAttribute('data-index') === snippetIndex &&
        (node.getAttribute('data-snippet') || '') === (snippetData || '')
      ) {
        const next = node.nextElementSibling;
        if (count < wantedCount) {
          consumedSpans.add(node);
        } else if (allowStructuralEdits) {
          node.remove();
        } else {
          consumedSpans.add(node); // can't prune this pass — at least keep it from being reprocessed
        }
        count++;
        node = next;
      }
    }

    const rect = span.getBoundingClientRect();
    const scrollTop = iframeDoc.documentElement.scrollTop || iframeDoc.body.scrollTop;
    const scrollLeft = iframeDoc.documentElement.scrollLeft || iframeDoc.body.scrollLeft;
    // rect.height is this span's line height, which scales with surrounding font size. Centering the
    // fixed-height bullet within that box (rather than a flat offset from its top), plus a small
    // extra upward nudge (text glyphs sit a bit higher within their own line box than dead-center),
    // keeps it vertically aligned regardless of font size. Gated-only: this is the entire vertical
    // adjustment for that path — computePositionBasedOnNeighbors is called with verticalOffset: 0
    // below so it doesn't add anything further. Legacy is untouched, still relying on that
    // function's own default flat offset exactly as before.
    // Centering a TAG_HEIGHT-tall bullet within a taller rect.height box means pushing its top
    // DOWN by half the difference — (rect.height - TAG_HEIGHT) / 2, not the other way around. The
    // reversed sign was the actual bug behind "big font too high, small font too low": for a big
    // font (large rect.height) the flipped formula produced a large negative offset (pushing up)
    // when it should have pushed down, and vice versa for a small font.
    const top = showSecondOrderReferences
      ? rect.top + scrollTop + (rect.height - TAG_HEIGHT) / 2 + ALIGNMENT_NUDGE
      : rect.top + scrollTop;
    const left = rect.left + scrollLeft;

    const resolvedEntries = citationEntries.map(
      ({ thumbnailUrl, page, name, snippet, isCustomReference, citationId, sourceKey, source, referenceFoundOnPage }) => {
        // Each distinct second-order source gets its own next-available number, reused if it repeats
        // later (same as Assist); bullets with no citationId fall back to the span's id-page key.
        // dictKey is never reserved upfront, so its number isn't burned when every entry has a citationId.
        const bulletDictKey = citationId || dictKey;
        if (!dict[bulletDictKey]) dict[bulletDictKey] = label++;

        return {
          id,
          page,
          label: dict[bulletDictKey],
          top,
          left,
          name,
          snippetData: snippet,
          thumbnailUrl,
          updatedByUser,
          isCustomReference,
          citationId, // in-memory identity for second-order bullets sharing one span; undefined otherwise
          sourceKey, // this source's own identity within the span; undefined for plain (non-second-order) bullets
          // { name, logo, type, unsubstantiated, isPrimarySource, docType } from resolveCitationSourceInfo,
          // or null for orgs without second-order references (see mapCitationsDataFromFootnote) — drives
          // the rounded-tag/enriched-card rendering below when showSecondOrderReferences is true.
          source,
          // Page this genuine second-order source's own referenceId resolves to (where the source
          // itself is found) — distinct from `page` above (the citing document's own page, used
          // for the passage row). undefined for primary-source/unsubstantiated/custom entries.
          referenceFoundOnPage,
          span, // Keep reference to the original span for removal
        };
      },
    );

    // A later entry can reuse an earlier, lower number (same source cited earlier), so raw order
    // doesn't guarantee ascending labels — sort before slicing into visible/overflow so both the
    // bullets and the "+N" tooltip list read in increasing order, same as Assist.
    resolvedEntries.sort((a, b) => (a.label ?? Infinity) - (b.label ?? Infinity));

    span.setAttribute('data-label', resolvedEntries[0].label);

    // Bullets from one span share a start point, so the generic collision-avoidance search would
    // scatter them out of label order via alternating +/- offsets. withGroupOffsets gives each a
    // deterministic left-to-right offset instead; collision-avoidance still applies for other spans.
    const groupLayout = showSecondOrderReferences
      ? { rowBoundary: getRowBoundary(span, scrollLeft), baseTop: top }
      : { rowBoundary: null, baseTop: top, getWidth: () => 24, gap: 2 };

    // Cap individually-drawn bullets at however many actually fit on this line (computeVisibleCount)
    // in the fallback path below, up to MAX_INDIVIDUAL_BULLETS either way — a busy snippet with many
    // second-order sources would otherwise clutter the document. The rest collapse into one "+N"
    // bullet whose tooltip lists them all, each still showing its own would-be numbering.
    const maxIndividual = Math.min(resolvedEntries.length, MAX_INDIVIDUAL_BULLETS);
    const hasOverflow = resolvedEntries.length > maxIndividual;
    // Constant regardless of the current overflow count, so reconcileCitationSiblings treats it as
    // the same ongoing entry across renders (no need to recreate the span just because N in "+N"
    // changed) — the overlay still recomputes the visible "+N" label fresh every render either way.
    const OVERFLOW_SOURCE_KEY = '__overflow__';
    const reconcileInput = resolvedEntries.slice(0, maxIndividual);
    if (hasOverflow) {
      // Gives the overflow badge its own real reserved span too (same mechanism as every other
      // bullet), instead of being positioned over space nothing reserves. Only `sourceKey` matters
      // to reconcileCitationSiblings — the rest of this placeholder entry is never read.
      reconcileInput.push({ sourceKey: OVERFLOW_SOURCE_KEY });
    }

    let reconciled = null;
    if (showSecondOrderReferences && allowStructuralEdits && setFieldContent && resolvedEntries.length > 1) {
      try {
        reconciled = await reconcileCitationSiblings(span, reconcileInput);
      } catch (e) {
        // Never let one citation's reconciliation failure (e.g. a rejected setContent round-trip)
        // abort the whole render pass — every other citation's bullets, already built or still to
        // come in this same loop, must still get a chance to render. Falls back to the safe math
        // below for just this one citation.
        console.error('Failed to reconcile citation siblings, falling back to simulated layout', e);
      }
    }

    let slotEntries;
    if (reconciled) {
      // Every entry — including the first, which used to just ride on the original anchor span's
      // own position — now has its own real citation span, each with its own real reserved
      // margin-right, 1:1 with reconciled (see reconcileCitationSiblings — no separate marker
      // element is kept around anymore).
      //
      // CONFIRMED (not just correlated): writing to a reconciled span's own `style` — even a purely
      // cosmetic, every-render-reapplied property like margin-right, never touching data-doc-id or
      // any other real content — reliably breaks it (reproduced after ruling out the stale-Wesley-
      // bundle red herring that muddied the first report). Something in Wesley's pipeline reacts
      // badly to style mutations on these spans specifically; exact mechanism unconfirmed, but do
      // not attempt this again without a fundamentally different approach (e.g. sizing the bullet to
      // fit the UNTOUCHED default margin instead of trying to widen the margin itself).

      const visible = resolvedEntries.slice(0, maxIndividual).map((entry, index) => {
        const elRect = reconciled[index].getBoundingClientRect();
        return {
          ...entry,
          top: showSecondOrderReferences
            ? elRect.top + scrollTop + (elRect.height - TAG_HEIGHT) / 2 + ALIGNMENT_NUDGE
            : elRect.top + scrollTop,
          // GROUP_LEADING_GAP: a span is a zero-width marker right where the preceding text ends —
          // without this, a bullet's left edge lands exactly there, reading as overlapping the last
          // character (same reasoning as the fallback path below, which already applies it; a real
          // reconciled span just never had the equivalent nudge).
          left: elRect.left + scrollLeft + GROUP_LEADING_GAP,
          tagWidth: getTagWidth(entry.label),
          span: reconciled[index],
        };
      });

      if (hasOverflow) {
        // The overflow badge now has its own real reserved span too (reconciled[maxIndividual],
        // via the OVERFLOW_SOURCE_KEY placeholder entry above) — read its true position the same
        // way as every other bullet, instead of a simulated offset from the last real one.
        const overflow = resolvedEntries.slice(maxIndividual);
        const overflowRect = reconciled[maxIndividual].getBoundingClientRect();
        const overflowLabel = `+${overflow.length}`;
        slotEntries = [
          ...visible,
          {
            id,
            page: resolvedEntries[0].page,
            top: showSecondOrderReferences
              ? overflowRect.top + scrollTop + (overflowRect.height - TAG_HEIGHT) / 2 + ALIGNMENT_NUDGE
              : overflowRect.top + scrollTop,
            left: overflowRect.left + scrollLeft + GROUP_LEADING_GAP,
            isCustomReference: resolvedEntries[0].isCustomReference,
            // reconciled[0] — entry 0's own real span, not the overflow span itself and not the
            // loop's own `span` (stale after setFieldContent). The remove-icon handler walks
            // forward from whichever span an isOverflow bullet points at and removes the whole
            // matching cluster, so this still means "remove the whole citation" when its header
            // trash is clicked, same as it always has; only the per-row icons inside its tooltip
            // (see isOverflow below) are granular.
            span: reconciled[0],
            isOverflow: true,
            label: overflowLabel,
            tagWidth: getTagWidth(overflowLabel),
            overflowEntries: overflow.map((entry) => ({
              label: entry.label,
              name: entry.name,
              sourceKey: entry.sourceKey,
              page: entry.page,
              snippetData: entry.snippetData,
              source: entry.source,
              referenceFoundOnPage: entry.referenceFoundOnPage,
            })),
          },
        ];
      } else {
        slotEntries = visible;
      }

      bulletInfos.push(...slotEntries);
      continue;
    }

    const visibleCount = computeVisibleCount(resolvedEntries, left, groupLayout.rowBoundary);
    if (resolvedEntries.length > visibleCount) {
      const visible = resolvedEntries.slice(0, visibleCount);
      const overflow = resolvedEntries.slice(visibleCount);
      const first = resolvedEntries[0];

      const overflowBadgeEntry = {
        id,
        page: first.page,
        top,
        left,
        isCustomReference: first.isCustomReference,
        span,
        isOverflow: true,
        label: `+${overflow.length}`,
        overflowEntries: overflow.map((entry) => ({
          label: entry.label,
          name: entry.name,
          sourceKey: entry.sourceKey,
          page: entry.page,
          snippetData: entry.snippetData,
          source: entry.source,
          referenceFoundOnPage: entry.referenceFoundOnPage,
        })),
      };
      // Positioned together (visible entries + the overflow badge) in one pass, so the badge's
      // offset accounts for the actual widths of all the visible bullets before it.
      slotEntries = [...visible, overflowBadgeEntry];
    } else {
      slotEntries = resolvedEntries;
    }

    bulletInfos.push(...withGroupOffsets(slotEntries, groupLayout));
  }

  // Superseded while the per-span loop above was running — #citation-bullets-overlay has already
  // been replaced by the later pass, so appending into our own `overlay` (now detached) would build
  // bullets that silently never render. Bail out instead of doing Step 2 at all.
  if (!isCurrent()) {
    return;
  }

  // Step 2: Batch append to DOM with collision detection
  const bulletElements = [];
  const tooltipElements = [];
  const bulletPositions = []; // Track positions to avoid overlaps
  const elementsBySpan = new Map(); // span -> [{ bullet, tooltip }] — a span can have several bullets
  // Every tooltip from this render pass, so opening one force-closes the rest (see showTooltip) —
  // without this, adjacent bullets could each stay "visible" if the mouse moved between them within
  // hideTooltip's 200ms grace window, leaving both open and overlapping.
  const allTooltips = [];

  bulletInfos.forEach((data) => {
    if (!data) {
      return;
    }

    const {
      id,
      page,
      label,
      top,
      left,
      name,
      snippetData,
      thumbnailUrl,
      updatedByUser,
      isCustomReference,
      citationId,
      isOverflow,
      overflowEntries,
      source,
      referenceFoundOnPage,
      span,
      tagWidth,
    } = data;

    const bullet = iframeDoc.createElement('div');
    if (citationId) {
      bullet.setAttribute('data-second-order-ref-id', citationId);
    }

    // Cross-cluster collision distance — matched to the single-digit tag width (24, legacy's own
    // value too). A double-digit/+N tag is wider (30) but still checked against this same distance;
    // left as-is since double-digit bullets are the less common case and this only governs when
    // bullets from DIFFERENT clusters are treated as overlapping, not spacing within one cluster
    // (see withGroupOffsets above, which already accounts for each bullet's own width).
    // verticalOffset: gated's own `top` (above) already IS the fully-desired vertical position, so
    // it passes 0 here rather than fighting the function's own default -4 (which legacy still relies
    // on, unchanged, by not passing this argument at all).
    const { bulletTop, bulletLeft } = computePositionBasedOnNeighbors(
      top,
      left,
      bulletPositions,
      24,
      showSecondOrderReferences ? 0 : undefined,
      showSecondOrderReferences,
    );

    // Store the final position
    bulletPositions.push({ top: bulletTop, left: bulletLeft });

    if (showSecondOrderReferences) {
      // Aligned with Discover's CitationInfoLabel.vue: solid border by default, dashed when
      // unsubstantiated — manually-added gets the same solid tag as any other citation. Icon and
      // number sit side by side in normal flow, not an absolute corner badge (fragile centering).
      bullet.className = 'citation-tag';
      // Manually-added is its own case, not the generic dashed/transparent "unsubstantiated"
      // treatment — solid border (in the same dark color, not the light grey a real,
      // substantiated source gets) over the usual grey fill, matching the tooltip's own pill.
      const isManuallyAdded = source?.type === 'Manually added';
      bullet.classList.toggle('citation-tag--manual', Boolean(!isOverflow && isManuallyAdded));
      bullet.classList.toggle(
        'citation-tag--unsubstantiated',
        Boolean(!isOverflow && !isManuallyAdded && source?.unsubstantiated),
      );

      if (!isOverflow && source?.logo) {
        // A plain span with the logo as a CSS background-image, not an <img> — Veeva's logo is a
        // base64 PNG wrapped in a pattern-filled SVG, and an <img src> both gets caught by this MJML
        // doc's ambient img resets and interacts unpredictably with the SVG's internal scaling.
        const icon = iframeDoc.createElement('span');
        icon.className = 'citation-tag-icon';
        icon.setAttribute('role', 'img');
        icon.setAttribute('aria-label', source.name || 'logo');
        const iconStyles = {
          position: 'static',
          width: '13px',
          height: '13px',
          'max-width': '13px',
          'max-height': '13px',
          'min-width': '13px',
          'min-height': '13px',
          margin: '0',
          padding: '0',
          'flex-shrink': '0',
          'border-radius': '50%',
          background: `#fff url("${source.logo}") center center / 9px 9px no-repeat`,
          border: `1px solid ${CITATION_TAG_COLORS.border}`,
          'box-sizing': 'border-box',
          display: 'block',
        };
        // Set every property via setProperty(..., 'important'): this MJML document's boilerplate can
        // carry !important resets of its own, and only an inline !important declaration is guaranteed
        // to beat those regardless of source order, unlike a class rule in our injected <style>.
        Object.entries(iconStyles).forEach(([prop, value]) => icon.style.setProperty(prop, value, 'important'));
        bullet.appendChild(icon);
      }

      const labelSpan = iframeDoc.createElement('span');
      labelSpan.textContent = label;
      ['display', 'line-height', 'margin', 'padding', 'vertical-align'].forEach((prop) =>
        labelSpan.style.setProperty(
          prop,
          { display: 'block', 'line-height': '1', margin: '0', padding: '0', 'vertical-align': 'middle' }[prop],
          'important',
        ),
      );
      bullet.appendChild(labelSpan);

      // Width matches the label's own content (see getTagWidth/withGroupOffsets above) — a
      // double-digit or "+N" label needs more room than the CSS class's default single-digit width.
      Object.assign(bullet.style, { top: `${bulletTop}px`, left: `${bulletLeft}px`, width: `${tagWidth}px` });
    } else {
      // Legacy purple/green circle — untouched for orgs without second-order references.
      bullet.className = 'info-bullet-wrapper';
      bullet.textContent = label;
      Object.assign(bullet.style, {
        top: `${bulletTop}px`,
        left: `${bulletLeft}px`,
        color: updatedByUser ? '#4CAF50' : '#6222e3',
        border: `2px solid ${updatedByUser ? '#4CAF50' : '#6222e3'}`,
      });
    }

    const removeIconSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path d="M7.5 7.5C7.77614 7.5 8 7.72386 8 8V10C8 10.2761 7.77614 10.5 7.5 10.5C7.22386 10.5 7 10.2761 7 10V8C7 7.72386 7.22386 7.5 7.5 7.5Z" fill="#C64444"/>
            <path d="M10 8C10 7.72386 9.77614 7.5 9.5 7.5C9.22386 7.5 9 7.72386 9 8V10C9 10.2761 9.22386 10.5 9.5 10.5C9.77614 10.5 10 10.2761 10 10V8Z" fill="#C64444"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.71429 4.5V3.1C5.71429 2.66935 5.8922 2.26039 6.20229 1.96194C6.51172 1.66411 6.92747 1.5 7.35714 1.5H9.64286C10.0725 1.5 10.4883 1.66411 10.7977 1.96194C11.1078 2.26039 11.2857 2.66935 11.2857 3.1V4.5H13.5C13.7761 4.5 14 4.72386 14 5C14 5.27614 13.7761 5.5 13.5 5.5H13V11.9C13 12.3306 12.8221 12.7396 12.512 13.0381C12.2026 13.3359 11.7868 13.5 11.3571 13.5H5.64286C5.21318 13.5 4.79743 13.3359 4.488 13.0381C4.17792 12.7396 4 12.3306 4 11.9V5.5H3.5C3.22386 5.5 3 5.27614 3 5C3 4.72386 3.22386 4.5 3.5 4.5H5.71429ZM6.89576 2.68243C7.01498 2.56768 7.18061 2.5 7.35714 2.5H9.64286C9.81939 2.5 9.98502 2.56768 10.1042 2.68243C10.2228 2.79655 10.2857 2.94717 10.2857 3.1V4.5H6.71429V3.1C6.71429 2.94717 6.77718 2.79655 6.89576 2.68243ZM5 5.5V11.9C5 12.0528 5.0629 12.2034 5.18147 12.3176C5.30069 12.4323 5.46633 12.5 5.64286 12.5H11.3571C11.5337 12.5 11.6993 12.4323 11.8185 12.3176C11.9371 12.2034 12 12.0528 12 11.9V5.5H5Z" fill="#C64444"/>
          </svg>
    `;

    const tooltip = iframeDoc.createElement('div');
    // Every gated card gets the same fixed width/height and scrolls internally, rather than growing
    // with content (previously producing visibly different popup sizes). Manually-added has no
    // found-on/passage/snippet body though, so tooltip-popup--compact lets it size to its own content.
    tooltip.className = `tooltip-popup${
      showSecondOrderReferences ? ` ${isCustomReference ? 'tooltip-popup--compact' : 'tooltip-popup--plain'}` : ''
    }`;

    if (isOverflow) {
      // Full-card content per row (matching Discover's overflow branch), not just a flat name line —
      // entries share this span's id/page, so reusing data-action="open-full-doc" per row is safe.
      // Each row also gets its own delete icon (data-overflow-index), separate from the header's remove-icon.
      const overflowRows = overflowEntries
        .map((entry, entryIndex) => {
          const labelsHtml = showSecondOrderReferences ? buildSourceLabelsRowHtml(entry.source) : '';
          const isGenuineSecondOrder =
            showSecondOrderReferences && !entry.source?.isPrimarySource && !entry.source?.unsubstantiated;
          const referenceFoundOnHtml = isGenuineSecondOrder
            ? buildReferenceFoundOnRowHtml({ name: entry.name, page: entry.referenceFoundOnPage })
            : '';
          const passageHtml = showSecondOrderReferences ? buildPassageRowHtml({ name: entry.name, page }) : '';
          // Wrapped in its own div so the snippet + "Read full passage" link stay grouped as one
          // unit alongside the other rows inside .tooltip-overflow-item-main's flex column.
          const snippetHtml = entry.snippetData
            ? `<div>
                 <blockquote class="tooltip-snippet-js">"...${truncateSnippet(entry.snippetData)}..."</blockquote>
                 <span class="tooltip-link tooltip-read-full" data-action="open-full-doc" data-page="${page}">Read full passage</span>
               </div>`
            : '';

          return `
            <div class="tooltip-overflow-item" data-overflow-index="${entryIndex}">
              <div class="tooltip-overflow-item-main">
                ${labelsHtml}
                <span class="tooltip-overflow-item-text tooltip-title tooltip-title--plain">${entry.label}. ${escapeHtml(entry.name || '')}</span>
                ${referenceFoundOnHtml}
                ${passageHtml}
                ${snippetHtml}
              </div>
              <div class="overflow-item-remove-icon">${removeIconSvg}</div>
            </div>`;
        })
        .join('');

      // Gated: no "N more references" header text — each row already carries its own title
      // (styled identically to the single-reference card's own title, see tooltip-overflow-item-text
      // above), so a generic count line above them was redundant. Legacy keeps its original header.
      const overflowHeaderTitleHtml = showSecondOrderReferences
        ? ''
        : `<div class="tooltip-title">${overflowEntries.length} more references</div>`;
      // No header-level remove icon here — only each row's own delete (below) removes anything from
      // the "+N" list. Gated has no header title either, so it drops the header entirely.
      const overflowHeaderHtml = showSecondOrderReferences
        ? ''
        : `<div class="tooltip-header">
            <div class="tooltip-text">
              ${overflowHeaderTitleHtml}
            </div>
          </div>`;
      tooltip.innerHTML = `
        ${overflowHeaderHtml}
        <div class="tooltip-overflow-list${showSecondOrderReferences ? ' tooltip-body' : ''}">${overflowRows}</div>
      `;
    } else if (showSecondOrderReferences) {
      // Aligned with InfoTooltipContent.vue/SecondOrderReferenceItem.vue: header + labels + found-on/
      // passage row + quote + "Read full passage". Manually-added has no externalId/snippet, so it gets
      // header + labels only; title hides when unsubstantiated, except manually-added (its text IS the title).
      const isUnsubstantiatedMainDocument = !isCustomReference && !source?.isPrimarySource && source?.unsubstantiated;
      const titleRowHtml = isUnsubstantiatedMainDocument
        ? ''
        : `<div class="tooltip-title tooltip-title--plain" ${isCustomReference ? '' : `data-action="open-full-doc" data-page="${page}"`}>${escapeHtml(name)}</div>`;
      const labelsRowHtml = buildSourceLabelsRowHtml(source);
      // Labels row (Internal · Veeva · Type, or "No citation found · ...") always above, the
      // title (or, when hidden, nothing) below it — same order as the "Manage references" popup,
      // for every case (real document, manually added, or unsubstantiated).
      const textContentHtml = `${labelsRowHtml}${titleRowHtml}`;
      tooltip.innerHTML = `
      <div class="tooltip-header tooltip-header--plain">
        ${thumbnailUrl ? `<img class="tooltip-image" src="${thumbnailUrl}" alt="thumb"/>` : ''}
        <div class="tooltip-text">
          ${textContentHtml}
        </div>
        <div class="remove-icon">${removeIconSvg}</div>
      </div>
      <div class="tooltip-body">
        ${
          !isCustomReference && !source?.isPrimarySource && !source?.unsubstantiated
            ? buildReferenceFoundOnRowHtml({ name, page: referenceFoundOnPage })
            : ''
        }
        ${!isCustomReference ? buildPassageRowHtml({ name, page }) : ''}
        ${
          !isCustomReference && snippetData
            ? `<blockquote class="tooltip-snippet-js">"...${truncateSnippet(snippetData)}..."</blockquote>
               <span class="tooltip-link tooltip-read-full" data-action="open-full-doc" data-page="${page}">Read full passage</span>`
            : ''
        }
      </div>
      `;
    } else {
      tooltip.innerHTML = `
      <div class="tooltip-header">
        ${thumbnailUrl ? `<img class="tooltip-image" src="${thumbnailUrl}" alt="thumb"/>` : ''}
        <div class="tooltip-text">
          <div class="tooltip-title">
            <a href="https://google.com/" target="_blank">${name}</a>
          </div>
          <div class="tooltip-subtext">Page ${page}</div>
        </div>
        <div class="remove-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path d="M7.5 7.5C7.77614 7.5 8 7.72386 8 8V10C8 10.2761 7.77614 10.5 7.5 10.5C7.22386 10.5 7 10.2761 7 10V8C7 7.72386 7.22386 7.5 7.5 7.5Z" fill="#C64444"/>
            <path d="M10 8C10 7.72386 9.77614 7.5 9.5 7.5C9.22386 7.5 9 7.72386 9 8V10C9 10.2761 9.22386 10.5 9.5 10.5C9.77614 10.5 10 10.2761 10 10V8Z" fill="#C64444"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.71429 4.5V3.1C5.71429 2.66935 5.8922 2.26039 6.20229 1.96194C6.51172 1.66411 6.92747 1.5 7.35714 1.5H9.64286C10.0725 1.5 10.4883 1.66411 10.7977 1.96194C11.1078 2.26039 11.2857 2.66935 11.2857 3.1V4.5H13.5C13.7761 4.5 14 4.72386 14 5C14 5.27614 13.7761 5.5 13.5 5.5H13V11.9C13 12.3306 12.8221 12.7396 12.512 13.0381C12.2026 13.3359 11.7868 13.5 11.3571 13.5H5.64286C5.21318 13.5 4.79743 13.3359 4.488 13.0381C4.17792 12.7396 4 12.3306 4 11.9V5.5H3.5C3.22386 5.5 3 5.27614 3 5C3 4.72386 3.22386 4.5 3.5 4.5H5.71429ZM6.89576 2.68243C7.01498 2.56768 7.18061 2.5 7.35714 2.5H9.64286C9.81939 2.5 9.98502 2.56768 10.1042 2.68243C10.2228 2.79655 10.2857 2.94717 10.2857 3.1V4.5H6.71429V3.1C6.71429 2.94717 6.77718 2.79655 6.89576 2.68243ZM5 5.5V11.9C5 12.0528 5.0629 12.2034 5.18147 12.3176C5.30069 12.4323 5.46633 12.5 5.64286 12.5H11.3571C11.5337 12.5 11.6993 12.4323 11.8185 12.3176C11.9371 12.2034 12 12.0528 12 11.9V5.5H5Z" fill="#C64444"/>
          </svg>
        </div>
      </div>
      <div class="tooltip-snippet">${snippetData || ''}</div>
      `;
    }

    let tooltipTop = top - 160;
    let tooltipLeft = left + 25;
    const tooltipWidth = 400; // must match .tooltip-popup's own width above
    const viewportWidth = iframeDoc.documentElement.clientWidth;
    if (tooltipTop < 0) tooltipTop = top + 10;
    if (tooltipLeft + tooltipWidth > viewportWidth) tooltipLeft = left - tooltipWidth;
    // The above only guards the right edge — for a bullet near the left edge, flipping to fit on the
    // right can itself push the (now 400px, up from 300px) tooltip past the left edge. Clamp back to
    // a small left margin instead of letting it run off-screen.
    if (tooltipLeft < 8) tooltipLeft = 8;

    Object.assign(tooltip.style, {
      top: `${tooltipTop + 80}px`,
      left: `${tooltipLeft}px`,
    });

    allTooltips.push(tooltip);

    let hideTimeout;
    const showTooltip = () => {
      clearTimeout(hideTimeout);
      // Force-close every other tooltip immediately (not via the normal 200ms-delayed
      // hideTooltip) before opening this one, so at most one is ever visible at a time — see the
      // comment on allTooltips above for why this matters.
      allTooltips.forEach((otherTooltip) => {
        if (otherTooltip !== tooltip) otherTooltip.classList.remove('visible');
      });
      tooltip.classList.add('visible');
    };
    const hideTooltip = () => {
      hideTimeout = setTimeout(() => tooltip.classList.remove('visible'), 200);
    };

    //click events
    if (showSecondOrderReferences) {
      // Click opens/pins the same card hover shows (matches Discover). Only elements explicitly
      // marked data-action="open-full-doc" navigate to the full document panel — manually-added never
      // gets one, so it stays click-inert beyond opening its own reduced card.
      bullet.addEventListener('click', showTooltip);
      tooltip.querySelectorAll('[data-action="open-full-doc"]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.stopPropagation();
          // Each element carries its own target page (see data-page above) — "REFERENCE FOUND ON"
          // points at the source's own page, distinct from the citing page every other action here
          // uses. Falls back to the citing `page` if data-page is somehow missing.
          const targetPage = el.dataset.page ? Number(el.dataset.page) : page;
          openDocument({ reference: { externalId: id }, page: targetPage });
        });
      });
    } else if (!isCustomReference) {
      bullet.addEventListener('click', () => {
        openDocument({ reference: { externalId: id }, page });
      });
      tooltip.addEventListener('click', () => {
        openDocument({ reference: { externalId: id }, page });
      });
    }

    //hover events
    bullet.addEventListener('mouseenter', showTooltip);
    bullet.addEventListener('mouseleave', hideTooltip);
    tooltip.addEventListener('mouseenter', showTooltip);
    tooltip.addEventListener('mouseleave', hideTooltip);

    // Track this bullet/tooltip pair against its source span so removal (below) can clean up
    // every bullet spawned from the same span (e.g. multiple second-order-reference bullets).
    if (!elementsBySpan.has(span)) {
      elementsBySpan.set(span, []);
    }
    elementsBySpan.get(span).push({ bullet, tooltip });

    // Add functionality to remove the span
    // Absent on the "+N" overflow tooltip by design (only its per-row deletes remove anything).
    const removeIcon = tooltip.querySelector('.remove-icon');
    removeIcon?.addEventListener('click', async (event) => {
      event.stopPropagation(); // Prevent triggering other click events
      const spanToRemove = span;
      if (!spanToRemove) return;

      // An individual bullet's own header trash removes the WHOLE citation: every real span sharing
      // this doc-id/page/index/snippet, not just this one. The "+N" tooltip has no header trash;
      // its per-row delete (below) is the granular path (excludes just one source via
      // data-excluded-second-order-refs).
      const matchesThisCluster = (el) =>
        el?.tagName === 'SPAN' &&
        el.getAttribute('data-doc-id') === spanToRemove.getAttribute('data-doc-id') &&
        el.getAttribute('data-page') === spanToRemove.getAttribute('data-page') &&
        el.getAttribute('data-index') === spanToRemove.getAttribute('data-index') &&
        (el.getAttribute('data-snippet') || '') === (spanToRemove.getAttribute('data-snippet') || '');

      // Walk back to the cluster's first span before collecting — clicking the 2nd/3rd bullet must
      // remove the whole cluster too. Removing only from the clicked span onward left the earlier
      // spans behind, and the next reconciliation pass just rebuilt the rest from footnote data.
      let clusterStart = spanToRemove;
      while (matchesThisCluster(clusterStart.previousElementSibling)) {
        clusterStart = clusterStart.previousElementSibling;
      }
      const spansToRemove = [];
      let node = clusterStart;
      while (matchesThisCluster(node)) {
        spansToRemove.push(node);
        node = node.nextElementSibling;
      }

      // Plain removal, no Wesley API involved — unlike inserting content Wesley's own model
      // doesn't know about (which a later Tiptap rebuild can discard, see reconcileCitationSiblings
      // above), removing a node has no such direction-of-truth problem: the next time this field is
      // parsed, it's parsed from whatever the live DOM already looks like, removed node included.
      // This is exactly how the original (pre-reconciliation) single-citation delete always worked.
      spansToRemove.forEach((el) => {
        el.remove();
        const pairs = elementsBySpan.get(el);
        if (pairs) {
          pairs.forEach(({ bullet: siblingBullet, tooltip: siblingTooltip }) => {
            siblingBullet.remove();
            siblingTooltip.remove();
          });
        }
      });

      updateEditorSelectableElementsData();
      // Persist the change: save iframe state then save to DB so the removal survives refresh
      setTimeout(async () => {
        try {
          refreshReferencesOnDelete();
          saveEditorContentStateFromIframe();
          if (typeof saveHtmlDocumentContentToDb === 'function') {
            await saveHtmlDocumentContentToDb();
          }
        } catch (e) {
          console.error('Failed to persist removed citation span', e);
        }
      }, 500);
    });

    // Per-row delete: excludes just that one source from this span (persisted as
    // data-excluded-second-order-refs) instead of removing the whole citation. The header's own
    // .remove-icon is untouched and still deletes everything.
    if (isOverflow) {
      overflowEntries.forEach((entry, entryIndex) => {
        const row = tooltip.querySelector(`[data-overflow-index="${entryIndex}"]`);
        const rowRemoveIcon = row?.querySelector('.overflow-item-remove-icon');
        if (!rowRemoveIcon || !entry.sourceKey) return;

        rowRemoveIcon.addEventListener('click', (event) => {
          event.stopPropagation();

          let excludedSourceKeys = [];
          try {
            excludedSourceKeys = JSON.parse(span.getAttribute('data-excluded-second-order-refs') || '[]');
          } catch {
            // malformed attribute value — keep the default empty array
          }
          if (!excludedSourceKeys.includes(entry.sourceKey)) {
            excludedSourceKeys.push(entry.sourceKey);
            span.setAttribute('data-excluded-second-order-refs', JSON.stringify(excludedSourceKeys));
          }

          row.remove();
          const remaining = tooltip.querySelectorAll('.tooltip-overflow-item').length;
          if (remaining > 0) {
            // Still other sources left in this citation's overflow group — just shrink the count.
            bullet.textContent = `+${remaining}`;
          } else {
            // That was the last one — nothing left to collapse, so the badge itself goes away too.
            // (Promoting the individually-shown bullets/renumbering is left to the next natural
            // re-render, same as any other data change here.)
            bullet.remove();
            tooltip.remove();
            const siblings = elementsBySpan.get(span);
            if (siblings) {
              elementsBySpan.set(
                span,
                siblings.filter((pair) => pair.bullet !== bullet),
              );
            }
          }

          updateEditorSelectableElementsData();
          setTimeout(async () => {
            try {
              refreshReferencesOnDelete();
              saveEditorContentStateFromIframe();
              if (typeof saveHtmlDocumentContentToDb === 'function') {
                await saveHtmlDocumentContentToDb();
              }
            } catch (e) {
              console.error('Failed to persist removed second-order source', e);
            }
          }, 500);
        });
      });
    }

    bulletElements.push(bullet);
    tooltipElements.push(tooltip);
  });

  // Append all at once to prevent layout thrashing
  bulletElements.forEach((el) => overlay.appendChild(el));
  tooltipElements.forEach((el) => overlay.appendChild(el));
};

const tagStyle = `
    * {
      font-family: 'DM Sans', sans-serif !important;
    }
    .info-bullet-wrapper {
      position: absolute;
      width: 24px;
      height: 24px;
      background-color: #fff;
      border: 2px solid #6222e3;
      color: #6222e3;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
      cursor: pointer;
      z-index: 10000;
      pointer-events: auto;
    }
    /* Fixed width, not min-width, so every tag (1 or 2 digit, with/without icon, "+N" badge) renders
       the same size instead of hugging its own content. box-sizing pinned explicitly since ambient
       MJML resets force border-box, which otherwise collapses the content box and clips the number. */
    .citation-tag {
      position: absolute;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0;
      width: 24px;
      height: 18px;
      padding: 0 1px;
      border-radius: 18px;
      border: 1px solid ${CITATION_TAG_COLORS.border};
      background: ${CITATION_TAG_COLORS.border};
      color: ${CITATION_TAG_COLORS.unsubstantiatedBorder};
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      cursor: pointer;
      z-index: 10000;
      pointer-events: auto;
      white-space: nowrap;
    }
    .citation-tag:hover {
      border: 1px solid ${CITATION_TAG_COLORS.unsubstantiatedBorder};
    }
    .citation-tag--unsubstantiated {
      border: 1px dashed ${CITATION_TAG_COLORS.unsubstantiatedBorder};
      background: transparent;
    }
    /* Manually-added: solid, dark border over the usual grey fill (from the base .citation-tag
       rule) — distinct from the dashed/transparent treatment every other "no citation found"
       bullet gets. */
    .citation-tag--manual {
      border: 1px solid ${CITATION_TAG_COLORS.unsubstantiatedBorder};
    }
    /* Source-logo icon beside the number in the in-text tag (separate from .citation-tag-logo used in
       the card's labels row). This class rule is documentation only — actual styling is set inline
       !important per element, since inline always beats an external stylesheet rule of equal importance. */
    .citation-tag-icon {
      width: 13px !important;
      height: 13px !important;
      max-width: 13px !important;
      max-height: 13px !important;
      min-width: 13px !important;
      min-height: 13px !important;
      margin: 0 !important;
      padding: 0 !important;
      flex-shrink: 0 !important;
      border-radius: 50% !important;
      background-color: #fff !important;
      background-size: 9px 9px !important;
      background-repeat: no-repeat !important;
      background-position: center center !important;
      border: 1px solid ${CITATION_TAG_COLORS.border} !important;
      box-sizing: border-box !important;
      display: block !important;
    }
    .citation-tag-logo {
      width: 16px !important;
      height: 16px !important;
      max-width: 16px !important;
      max-height: 16px !important;
      min-width: 0 !important;
      min-height: 0 !important;
      object-fit: contain !important;
      display: inline-block !important;
    }
    /* Same visual language as .citation-tag but inline, used in the card's labels row (mirrors
       ReferenceDetailsLabels.vue's CitationInfoLabel usage). !important throughout since this MJML
       document's own boilerplate can out-rank a plain declaration at any nesting depth. */
    .citation-pill {
      display: inline-flex !important;
      align-items: center !important;
      gap: 2px !important;
      height: 16px !important;
      padding: 4px 8px !important;
      border-radius: 24px !important;
      border: 1px solid ${CITATION_TAG_COLORS.border} !important;
      background: ${CITATION_TAG_COLORS.border} !important;
      color: ${CITATION_TAG_COLORS.unsubstantiatedBorder} !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      line-height: 16px !important;
      white-space: nowrap !important;
    }
    .citation-pill--unsubstantiated {
      border: 1px dashed ${CITATION_TAG_COLORS.unsubstantiatedBorder} !important;
      background: transparent !important;
    }
    /* Same font as .tooltip-source-name (Veeva) — capitalized, medium weight — scoped to just the
       type word so the "No citation found" prefix beside it (see buildSourceLabelsRowHtml) is
       unaffected. */
    .tooltip-pill-type {
      text-transform: capitalize !important;
      font-weight: 500 !important;
      color: ${CITATION_TAG_COLORS.neutral2} !important;
    }
    .tooltip-popup {
      position: absolute;
      /* !important: this MJML document's own boilerplate can carry width resets on generic divs
         (same reason established throughout this file) — without it, the popup itself can render
         narrower than 400px, which no child fix (however wide it's told to be) can compensate for. */
      width: 400px !important;
      max-width: none !important;
      background-color: white;
      border: 1px solid #ddd;
      border-radius: 8px;
      box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
      padding: 0;
      display: none;
      flex-direction: column;
      z-index: 10001;
      pointer-events: auto;
    }
    .tooltip-popup.visible {
      display: flex;
    }
    /* Every gated card shares one fixed width/height, unlike the legacy tooltip (sized by content) —
       .tooltip-header stays fixed at top, while variable content (found-on/passage/snippet/overflow)
       scrolls inside .tooltip-body instead of resizing the popup. */
    .tooltip-popup--plain {
      /* Matches Discover's own card (InfoTooltipContent.vue's .container: max-height: 232px). */
      height: 232px !important;
      overflow: hidden !important;
    }
    /* Manually-added has no found-on/passage/snippet body, just the header — sizes to content instead
       of the fixed height other cards use, with a modest max-height cap as a safety net since there's
       no separate scrolling body here to absorb overflow. */
    .tooltip-popup--compact {
      height: auto !important;
      max-height: 140px !important;
      overflow: hidden !important;
    }
    .tooltip-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }
    .tooltip-header {
      display: flex !important;
      flex-direction: row !important;
      align-items: flex-start !important;
      width: 100% !important;
      max-width: none !important;
      box-sizing: border-box !important;
      background-color: #e8e7e5;
      /* !important: an ambient MJML reset enlarging this padding would eat into the available content
         width without changing the visible box size — looking exactly like "title wraps too early"
         while the outer popup still measures 400px. */
      padding: 12px 12px 4px 12px !important;
      margin-bottom: -2px;
      border-radius: 7px 7px 0 0;
    }
    /* Discover's own card (InfoTooltipContent.vue) has no grey header backdrop — applied only when
       second-order references are on, so the legacy tooltip's header keeps its grey background
       unchanged. */
    .tooltip-header--plain {
      background-color: transparent !important;
    }
    .tooltip-image {
      width: 60px;
      height: 48px;
      background-color: #ccc;
      margin-right: 8px;
    }
    .tooltip-text {
      flex-grow: 1 !important;
      flex-basis: 0 !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
    }
    .tooltip-title {
      font-weight: bold;
      color: #6222e3;
      font-size: 13px;
      margin-bottom: 4px;
      cursor: pointer;
      max-height: 130px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      a {
        text-decoration: none !important;
      }
    }
    /* Plain block + max-height (not -webkit-line-clamp) lets white-space:normal collapse literal
       newlines from a manually-added reference's multi-line textarea. An earlier "wraps too early" bug
       traced to MJML's ambient max-width:200px on generic divs, which beats any width/min-width fix. */
    .tooltip-title--plain {
      display: block !important;
      color: ${CITATION_TAG_COLORS.unsubstantiatedBorder} !important;
      width: 100% !important;
      max-width: none !important;
      white-space: normal !important;
      /* Bounded by a plain max-height (~3 lines at this font-size/line-height) instead of
         -webkit-line-clamp — no ellipsis on truncation, but the header can no longer grow tall
         enough to squeeze .tooltip-body down to nothing inside the popup's fixed height. */
      overflow: hidden !important;
      line-height: 20px !important;
      max-height: 60px !important;
      /* Discover's own .second-order-title uses medium weight, not the legacy .tooltip-title's bold. */
      font-weight: 500 !important;
      /* A title built from a URL can contain one long unbreakable "word" — without this, normal
         wrapping tries to move the whole URL to its own line, then clips it instead of wrapping like
         .tooltip-snippet-js's plain-text passages do. */
      overflow-wrap: break-word !important;
      word-break: break-word !important;
    }
    .tooltip-subtext {
      line-height: 16px;
      font-weight: 500;
      font-style: normal;
      color: #8e8d8b;
      letter-spacing: .1px;
      font-size: 13px;
    }
    /* ReferenceDetailsLabels.vue equivalent: type pill + doc name (+logo) + doc type, wrapped.
       !important throughout for the same reason as .citation-pill above. */
    .tooltip-labels {
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      gap: 4px !important;
      margin-bottom: 4px !important;
      font-size: 11px !important;
      color: ${CITATION_TAG_COLORS.neutral2} !important;
      width: 100% !important;
      max-width: none !important;
    }
    .tooltip-source-name {
      display: inline-flex !important;
      align-items: center !important;
      gap: 4px !important;
      font-weight: 500 !important;
      text-transform: capitalize !important;
      white-space: nowrap !important;
      color: ${CITATION_TAG_COLORS.neutral2} !important;
    }
    .tooltip-doc-type {
      text-transform: capitalize !important;
      font-weight: 500 !important;
      color: ${CITATION_TAG_COLORS.neutral2} !important;
    }
    /* ReferenceDetails.vue equivalent: "REFERENCE FOUND ON"/"PASSAGE 1" · name > p.N */
    .tooltip-passage-row {
      display: flex !important;
      align-items: center !important;
      flex-wrap: wrap !important;
      gap: 4px !important;
      padding: 0 12px !important;
      margin-top: 4px !important;
      cursor: pointer !important;
    }
    .tooltip-subtitle {
      text-transform: uppercase !important;
      color: ${CITATION_TAG_COLORS.neutral2} !important;
      font-size: 10px !important;
      font-weight: 700 !important;
    }
    .tooltip-link {
      color: #6222e3 !important;
      font-size: 11px !important;
      cursor: pointer !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      max-width: 260px !important;
    }
    .tooltip-read-full {
      display: block !important;
      padding: 0 12px 8px 12px !important;
      max-width: none !important;
    }
    /* Base rule, still relied on by the legacy (non-gated) tooltip, whose snippet is raw unbounded
       text with no JS truncation — this line-clamp is the only thing limiting it. !important
       throughout since MJML boilerplate can carry its own !important resets on blockquote/generic elements. */
    .tooltip-snippet {
      font-size: 12px !important;
      line-height: 1.5 !important;
      padding: 8px 12px !important;
      margin: 0 !important;
      color: #333 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      display: -webkit-box !important;
      -webkit-line-clamp: 3 !important;
      -webkit-box-orient: vertical !important;
    }
    /* Used only by gated cards, whose snippet is already length-bounded in JS (truncateSnippet) — a
       standalone class, not a modifier on .tooltip-snippet. Cascading a later rule to "unset" that
       rule's -webkit-line-clamp isn't reliably honored across browsers, corrupting box height. */
    .tooltip-snippet-js {
      font-size: 12px !important;
      line-height: 1.5 !important;
      padding: 8px 12px !important;
      margin: 0 !important;
      color: #333 !important;
      display: block !important;
    }
    .remove-icon {
      position: absolute;
      right: 4px;
      top: 4px;
      font-size: 12px;
      cursor: pointer;
      z-index: 10001;
    }
    .tooltip-overflow-list {
      padding: 8px 12px 12px 12px;
      max-height: 340px;
      overflow-y: auto;
    }
    .tooltip-overflow-item {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
      font-size: 12px !important;
      line-height: 1.5;
      color: #333 !important;
      padding: 8px 0;
    }
    .tooltip-overflow-item + .tooltip-overflow-item {
      border-top: 1px solid #e8e7e5;
    }
    .tooltip-overflow-item-text {
      flex: 1;
      min-width: 0;
    }
    .tooltip-overflow-item-main {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    /* These normally carry their own horizontal padding (the single-card layout's container has
       none). Inside an overflow row, .tooltip-overflow-list already provides that padding, so keeping
       both would double-indent the passage/snippet lines relative to the row above them. */
    .tooltip-overflow-item-main .tooltip-passage-row {
      padding: 0 !important;
    }
    .tooltip-overflow-item-main .tooltip-snippet-js {
      padding: 4px 0 0 0 !important;
    }
    .tooltip-overflow-item-main .tooltip-read-full {
      padding: 4px 0 0 0 !important;
    }
    .overflow-item-remove-icon {
      flex-shrink: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
    }
  `;

export const isCitationBulletVisibleOnDoc = (el) => {
  let node = el;

  while (node) {
    if (node.hasAttribute && node.hasAttribute('hidden')) {
      return false;
    }

    if (node.getAttribute) {
      const style = node.getAttribute('style') || '';
      if (/display\s*:\s*none/.test(style) || /visibility\s*:\s*hidden/.test(style)) {
        return false;
      }
    }

    node = node.parentElement;
  }

  return true;
};
